import { config, isFeatureEnabled } from '@iheartjane/config';

/* eslint-disable-next-line no-restricted-imports */
import type { Cart, CartLineItems, PosCart } from '@jane/shared/models';

export type { CartLineItems };
interface GetCartLineItemsProps {
  cart: Cart;
  posCartTotals?: PosCart['totals'];
  tipAmount: number;
}

function getPosCartRoundingAmount(
  posCartTotals: PosCart['totals'],
  storeId: string | number | undefined | null
) {
  if (!storeId) return 0;
  const roundingDisplayEnabled =
    isFeatureEnabled('roundingDisplay') &&
    config.roundingBetaStoreIds.includes(parseInt(storeId.toString()));

  if (!roundingDisplayEnabled) {
    return 0;
  }

  const roundingAmount = posCartTotals!.rounding_amount;
  const HALF_PENNY = 0.005;
  const atLeastPennyRounded = Math.abs(Number(roundingAmount)) >= HALF_PENNY;

  if (!atLeastPennyRounded) {
    return 0;
  }

  return roundingAmount;
}

// note: rounding amount behaves as follows:
//   if there is a PosCart, the store has enabled the roundingDisplay feature, and the rounding amount is at least 0.005, then the rounding amount is displayed
//   if there is no PosCart, the rounding amount is taken directly from the cart.
//   UI components can simply check if the rounding amount is not 0 to determine if it should be displayed.
export const getCartLineItems = ({
  cart,
  posCartTotals,
  tipAmount,
}: GetCartLineItemsProps): CartLineItems => {
  const {
    all_discounts_total: discountTotal,
    cart_discount_amount: cartLevelDiscountTotal,
    delivery_fee_amount: deliveryFee,
    discounted_product_total: discountedProductTotal,
    discounted_subtotal: discountedSubtotal,
    item_total: preDiscountSubtotal,
    order_total,
    product_discounts_total: productDiscountTotal,
    rounding_amount: roundingAmount,
    sales_tax_amount,
    service_fee_amount: serviceFee,
    store_tax_amount,
  } = cart;

  if (posCartTotals) {
    return {
      preDiscountSubtotal,
      discountedProductTotal,
      discountedSubtotal,
      productDiscountTotal,
      cartLevelDiscountTotal,
      discountTotal,
      deliveryFee,
      roundingAmount: getPosCartRoundingAmount(posCartTotals, cart.store?.id),
      serviceFee,
      salesTax: posCartTotals.sales_tax,
      storeTax: posCartTotals.store_tax,
      tipAmount,
      total: (posCartTotals?.total ?? 0) + tipAmount,
    };
  }

  return {
    preDiscountSubtotal,
    discountedProductTotal,
    discountedSubtotal,
    productDiscountTotal,
    cartLevelDiscountTotal,
    discountTotal,
    deliveryFee,
    serviceFee,
    salesTax: sales_tax_amount,
    storeTax: store_tax_amount,
    tipAmount,
    roundingAmount,
    total: (order_total ?? 0) + tipAmount,
  };
};
